import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import errorBoundary from '@ifeng-fe/errorBoundary';
import { dialog, message, Sheet } from '@src/mobile/components/ModalBox';
import { groundsClose, isGrounds, userIsLogin, userLogin } from '@src/public/utils/app';
import { useMaximize } from '@src/public/hooks/app';
import { smoothScroll } from '@src/public/utils';
import { useFixedTitle } from './hooks';
import { cancelFollowTimeline, followTimeline } from '../service';
import { ListStore } from '../store';
import styles from './index.css';
import More from './More';
import Live from './Live';
import IframeVideo from '../IframeVideo';
import StatusBar from '@src/mobile/components/StatusBar';
import { jsonp } from '@ifeng-fe/ui_base';

dayjs.extend(isSameOrAfter);

const cx = classNames.bind(styles);

const Nav = () => {
    const listStore = ListStore.useContainer();
    const { listStates, changeListStates } = listStore;
    const { id, title, follow, live } = listStates;

    const statusBarHeight = useMaximize();

    const handleFollowClick = async () => {
        // 端内且未登录的情况下点击登录
        if (isGrounds && !userIsLogin()) {
            userLogin();

            return;
        }

        if (follow) {
            dialog({
                content: '取消追踪，无法跟进时间更新',
                okText: '不了吧',
                cancelText: '取消追踪',
                onCancel: async () => {
                    try {
                        await cancelFollowTimeline(id);
                        changeListStates({ follow: false });
                        message({ content: '已取消追踪' });
                    } catch (e) {
                        console.error(e);
                    }
                },
            });
        } else {
            try {
                await followTimeline(id);
                changeListStates({ follow: true });
                message({
                    content: (
                        <div style={{ textAlign: 'center' }}>
                            追踪成功<br />在我-【消息】中收取事件最新进展
                        </div>
                    ),
                });
            } catch (e) {
                console.error(e);
            }
        }
    };

    const barRef = useRef();
    const navBgRef = useRef();
    const [barHeight, setBarHeight] = useState(0);
    const [navBgHeight, setNavBgHeight] = useState(0);
    const [liveData, setLiveData] = useState({});

    const showFixedTitle = useFixedTitle(navBgHeight);

    useEffect(
        () => {
            if (barRef.current) {
                setBarHeight(barRef.current.offsetHeight);
                changeListStates({ navHeight: barRef.current.offsetHeight });
            }
            if (navBgRef.current) {
                setNavBgHeight(navBgRef.current.offsetHeight);
            }
        },
        [statusBarHeight],
    );

    const [showMore, setShowMore] = useState(false);

    const closeMore = () => {
        setShowMore(false);
    };

    const handleMoreClick = () => {
        setShowMore(true);
    };

    const handleBackClick = () => {
        groundsClose();
    };

    const [liveInfo, setLiveInfo] = useState(null);

    useEffect(
        () => {
            if (live) {
                const { startTime, endTime, liveUrl, showLive } = live;

                if (showLive && liveUrl && startTime && endTime) {
                    const startTimeObj = dayjs(startTime);
                    const endTimeObj = dayjs(endTime);

                    if (startTimeObj.isValid() && endTimeObj.isValid()) {
                        const nowTimeObj = dayjs();

                        if (nowTimeObj.isSameOrAfter(startTimeObj) && nowTimeObj.isBefore(endTimeObj)) {
                            setLiveInfo(live);
                        }
                    }
                }
            }
        },
        [live],
    );

    useEffect(
        () => {
            if (liveInfo && navBgRef.current) {
                setNavBgHeight(navBgRef.current.offsetHeight);
            }
        },
        [liveInfo],
    );

    const getLiveData = async liveId => {
        // 用上一步的短链生成二维码
        const res = await jsonp(`https://shankapi.ifeng.com/api/timeline/live/${liveId}/info/getLiveDataCallBack`, {
            jsonpCallback: 'getLiveDataCallBack',
            cache: false,
        });

        if (res?.data?.liveId) {
            setLiveData(res.data);
        }
    };

    useEffect(
        () => {
            if (listStates?.liveInfo?.liveId) {
                getLiveData(listStates.liveInfo.liveId);
            }
        },
        [listStates?.liveInfo?.liveId],
    );

    return (
        <>
            {isGrounds ? (
                <div className={styles.barWrap} ref={barRef}>
                    <StatusBar statusBarHeight={statusBarHeight} />
                    <div className={styles.bar}>
                        <div className={styles.back} onClick={handleBackClick} />
                        <div className={styles.barTitle} style={{ opacity: showFixedTitle ? 1 : 0 }}>
                            #{title}
                        </div>
                        <div className={cx('follow', { followed: follow })} onClick={handleFollowClick}>
                            <div className={styles.followText}>{follow ? '已追踪' : '追踪'}</div>
                        </div>
                        <div className={styles.more} onClick={handleMoreClick} />
                        <Sheet show={showMore} onClose={closeMore} dragToClose={false}>
                            <More close={closeMore} />
                        </Sheet>
                    </div>
                </div>
            ) : null}
            {liveData?.liveId ? <IframeVideo liveData={liveData} /> : null}
            <div
                className={`${styles.navBg} ${!isGrounds ? styles.navBgH5 : ''}`}
                style={{
                    paddingTop: barHeight,
                    marginTop: -barHeight,
                }}
                ref={navBgRef}>
                {liveInfo ? <Live content={{ liveInfo, showFixedTitle }} /> : null}
                <div className={cx('title', { titleBelowLive: !!liveInfo })}>#{title}</div>
            </div>

            <div
                className={cx('backToTop', { backToTopShow: showFixedTitle })}
                onClick={() => {
                    smoothScroll(window, 0);
                }}
            />
        </>
    );
};

export default errorBoundary(Nav);
